import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  PaperProps,
  SxProps,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { JSXElementConstructor, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../../app/constants";
import { CustomButton } from "../customButton/CustomButton";
import { useMemoizedDraggablePaper } from "./useMemoizedDraggablePaper";

type Props = {
  open: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  title: string | ReactElement;
  content: ReactElement;
  PaperProps?: PaperProps;
  dialogTitleStyles?: SxProps;
  disableBackdropClick?: boolean;
  dialogContentText?: string | ReactElement;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  TransitionProps?: TransitionProps;
  noBorder?: boolean;
  hideActions?: boolean;
  draggable?: boolean;
  dragHandleSelector?: string;
  PaperComponent?: JSXElementConstructor<PaperProps>;
  variant?: "default" | "white";
};

const BasicDialog = ({
  open,
  onCancel,
  onConfirm,
  title,
  content,
  PaperProps = {},
  dialogTitleStyles = {},
  dialogContentText,
  confirmLabel,
  confirmDisabled = false,
  TransitionProps = {},
  noBorder,
  hideActions = false,
  draggable = true,
  dragHandleSelector = DEFAULT_DRAG_HANDLE_SELECTOR,
  variant = "default",
}: Props) => {
  const { t } = useTranslation();

  const cursor = draggable ? "grab" : "auto";
  const isWhite = variant === "white";

  const MemoizedDraggablePaper = useMemoizedDraggablePaper(
    draggable,
    dragHandleSelector,
  );

  return (
    <Dialog
      open={open}
      PaperComponent={MemoizedDraggablePaper}
      PaperProps={{
        ...PaperProps,
        sx: {
          borderRadius: "12px",
          ...(PaperProps?.sx || {}),
          backgroundColor: isWhite ? "#fff" : "#EAEAF1",
        },
      }}
      TransitionProps={TransitionProps}
    >
      <DialogTitle
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{
          padding: isWhite ? "1rem 1rem 0rem 1rem" : "1rem",
          lineHeight: isWhite ? "1" : "1.6",
          cursor,
          ...dialogTitleStyles,
        }}
      >
        <Typography fontWeight={600} fontSize="23px" variant="header">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          backgroundColor: "#fff",
          padding: "0 1rem 1rem 1rem",
        }}
      >
        {dialogContentText && (
          <DialogContentText pt={2} color="textPrimary">
            {dialogContentText}
          </DialogContentText>
        )}
        <Box className={NO_DRAG_SELECTOR} pt={2}>
          {content}
        </Box>
        {!noBorder && (
          <Divider
            sx={{
              borderColor: "#4A1D9C1F",
              marginTop: "24px",
              marginBottom: "5px",
            }}
          />
        )}
      </DialogContent>
      {!hideActions && (
        <DialogActions
          className={DEFAULT_DRAG_HANDLE_SELECTOR}
          sx={{
            bgcolor: "#fff",
            display: "flex",
            justifyContent: "space-between",
            padding: "0 1rem 1rem 1rem",
            "& > button": {
              borderRadius: "18px",
            },
            cursor,
          }}
        >
          <CustomButton
            startIcon={<CloseIcon />}
            variant="outlined"
            onClick={onCancel}
          >
            {t("common:cancelShort")}
          </CustomButton>
          <CustomButton
            startIcon={<CheckIcon />}
            disabled={confirmDisabled}
            onClick={onConfirm}
            variant="contained"
          >
            {confirmLabel || t("common:save")}
          </CustomButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BasicDialog;
